import { z } from 'zod'
import {
  EntityItemArraySchema,
  OptionalStringSchema,
  IdentifierOnlyEntityItemSchema,
  GeoSchema,
  OptionalNumberSchema,
  ISODateTimeOptionalSchema,
  OrganizationSchema,
} from './entity'

const costSchema = z.object({
  costCompany: z.string(),
  costYear: z.number(),
  networkCostKW: z.number(),
  poiCostKW: z.number(),
  totalCostKW: z.number(),
})

const poiSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  kV: OptionalStringSchema,
  meta: OptionalStringSchema,
  poiGroup: z.string(),
})

const keyPeopleSchema = z.object({
  created: ISODateTimeOptionalSchema,
  email: OptionalStringSchema,
  fullName: OptionalStringSchema,
  id: OptionalStringSchema,
  linkedinUrl: OptionalStringSchema,
  phone: OptionalStringSchema,
  roles: z.array(OptionalStringSchema),
  source: OptionalStringSchema,
})

const milestoneEntrySchema = z.object({
  daysInQueue: OptionalNumberSchema,
  delta: OptionalNumberSchema,
  durationInDays: OptionalNumberSchema,
  eventHash: z.string(),
  fromValue: OptionalStringSchema,
  label: z.string(),
  reportDate: z.string(),
  stage: z.string(),
  toValue: z.string(),
})

const milestoneSchema = z.object({
  compositeKey: z.string(),
  daysInQueue: OptionalNumberSchema,
  delta: OptionalNumberSchema,
  durationInDays: OptionalNumberSchema,
  entries: z.array(milestoneEntrySchema),
  eventCount: z.number(),
  eventHash: z.string(),
  fromValue: OptionalStringSchema,
  key: z.string(),
  label: z.string(),
  reportDate: z.string(),
  searchKey: z.string(),
  set: z.number(),
  stage: z.string(),
  state: z.string(),
  topic: z.string(),
  toValue: z.string(),
  type: z.string(),
})

const eventTypeSchema = z.enum(['add', 'update'])

const propTypeSchema = z.enum(['applicationTags', 'tracked'])

const toFromSchema = z.object({
  assigned: z.string(),
  value: z.string(),
})

const transitionSchema = z.object({
  delta: z.number().optional(),
  durationInDays: z.number(),
  from: toFromSchema,
  to: toFromSchema,
})

const eventSchema = z.object({
  applicationId: z.string(),
  daysInQueue: OptionalNumberSchema,
  eventHash: OptionalStringSchema,
  eventType: eventTypeSchema,
  id: z.string(),
  propKey: z.string(),
  propType: propTypeSchema,
  propVal: z.string(),
  queueDate: z.string(),
  queueKey: z.string(),
  reportDate: z.string(),
  set: z.number(),
  transition: transitionSchema.optional(),
})

const queueSignalsSchema = z.object({
  filingDate: z.string(),
  headline: z.string(),
  id: z.string(),
  publishedDate: z.string(),
  subType: z.string(),
  type: z.string(),
})

const queueDocumentSchema = z.object({
  date: ISODateTimeOptionalSchema,
  document_id: OptionalStringSchema,
  filing_id: OptionalStringSchema,
  isSignal: z.boolean(),
  key: OptionalStringSchema,
  type: z.string().array(),
  url: OptionalStringSchema.nullable(),
})

// actual schemas
const baseQueueSchema = z.object({
  id: z.string(),
  schema: z.string(),
})

const baseEUQueueSchema = baseQueueSchema.extend({
  schema: z.literal('eu_queue'),
})

const baseUSQueueSchema = baseQueueSchema.extend({
  schema: z.literal('us_queue'),
})

const euQueueListSchema = baseEUQueueSchema
  .extend({
    actualOperationalDate: ISODateTimeOptionalSchema,
    allSectors: OptionalStringSchema,
    applicationId: OptionalStringSchema,
    applicationStatus: OptionalStringSchema,
    country: OptionalStringSchema,
    geo: GeoSchema.optional(),
    interconnectingEntity: OptionalStringSchema,
    lastModified: ISODateTimeOptionalSchema,
    localCounty: OptionalStringSchema,
    localState: OptionalStringSchema,
    organizations: OrganizationSchema,
    plannedOperationalDate: ISODateTimeOptionalSchema,
    projectName: OptionalStringSchema,
    queueDataset: OptionalStringSchema,
    queueDate: ISODateTimeOptionalSchema,
    sectors: EntityItemArraySchema,
    status: OptionalStringSchema,
    totalMw: OptionalNumberSchema,
  })
  .strict()

const usQueueListSchema = baseUSQueueSchema
  .extend({
    actualOperationalDate: ISODateTimeOptionalSchema,
    allSectors: OptionalStringSchema,
    applicationId: OptionalStringSchema,
    applicationStatus: OptionalStringSchema,
    applicationTags: z.array(IdentifierOnlyEntityItemSchema).optional(),
    country: EntityItemArraySchema.optional(),
    geo: GeoSchema.optional(),
    interconnectingEntity: OptionalStringSchema,
    lastModified: ISODateTimeOptionalSchema,
    localCounty: EntityItemArraySchema.optional(),
    localState: EntityItemArraySchema.optional(),
    organizations: OrganizationSchema,
    plannedOperationalDate: ISODateTimeOptionalSchema,
    poiLocation: OptionalStringSchema.or(OptionalNumberSchema),
    projectName: OptionalStringSchema,
    queueDataset: OptionalStringSchema,
    queueDate: ISODateTimeOptionalSchema,
    sectors: EntityItemArraySchema,
    totalMw: OptionalNumberSchema,
  })
  .strict()

const euQueueDetailsSchema = baseEUQueueSchema
  .extend({
    $organizations: OptionalStringSchema,
    allSectors: OptionalStringSchema,
    applicationId: OptionalStringSchema,
    applicationStatus: OptionalStringSchema,
    country: OptionalStringSchema,
    lastModified: ISODateTimeOptionalSchema,
    localCounty: OptionalStringSchema,
    localState: OptionalStringSchema,
    localTown: OptionalStringSchema,
    mwNet: OptionalNumberSchema,
    organizations: OrganizationSchema,
    projectName: OptionalStringSchema,
    queueDataset: OptionalStringSchema,
    queueDate: ISODateTimeOptionalSchema,
    refId: OptionalStringSchema,
    sectors: EntityItemArraySchema,
    status: OptionalStringSchema,
    storageCapacity: OptionalNumberSchema,
    technology: OptionalStringSchema,
    totalMw: OptionalNumberSchema,
    windFarmName: OptionalStringSchema,
  })
  .strict()

const usQueueDetailsSchema = baseUSQueueSchema
  .extend({
    $organizations: OptionalStringSchema,
    actualOperationalDate: ISODateTimeOptionalSchema,
    allSectors: OptionalStringSchema,
    applicationId: OptionalStringSchema,
    applicationTags: z.array(IdentifierOnlyEntityItemSchema).optional(),
    applicationType: OptionalStringSchema,
    applicationStatus: OptionalStringSchema,
    cost: costSchema.optional(),
    country: EntityItemArraySchema.optional(),
    dateCreated: ISODateTimeOptionalSchema,
    documents: z.array(queueDocumentSchema).optional(),
    events: z.array(eventSchema).optional(),
    isoRtoConnection: EntityItemArraySchema.optional(),
    keyPeople: keyPeopleSchema.optional(),
    localCounty: EntityItemArraySchema.optional(),
    localState: EntityItemArraySchema.optional(),
    milestones: z.array(milestoneSchema).optional(),
    organizations: OrganizationSchema,
    plannedOperationalDate: ISODateTimeOptionalSchema,
    poi: poiSchema.optional(),
    poiLocation: OptionalStringSchema.or(OptionalNumberSchema),
    projectName: OptionalStringSchema,
    queueDataset: OptionalStringSchema,
    queueDate: ISODateTimeOptionalSchema,
    reportCount: OptionalNumberSchema,
    reportDate: OptionalStringSchema,
    sectors: EntityItemArraySchema,
    signals: z.array(queueSignalsSchema).optional(),
    totalMw: OptionalNumberSchema,
    withdrawnDate: ISODateTimeOptionalSchema,
  })
  .strict()

// Queue details schemas
const queueDetailsSchema = euQueueDetailsSchema.or(usQueueDetailsSchema)
export const queueDetailsUnionSchema = z.discriminatedUnion('schema', [
  euQueueDetailsSchema,
  usQueueDetailsSchema,
])

export type QueueDetailsType = z.infer<typeof queueDetailsSchema>
export type QueueDetailsUnionType = z.infer<typeof queueDetailsUnionSchema>

export type EUQueueDetailsType = z.infer<typeof euQueueDetailsSchema>
export type USQueueDetailsType = z.infer<typeof usQueueDetailsSchema>

// Queue list schemas
const euQueueListWithSortSchema = euQueueListSchema.extend({
  sort: z.tuple([z.number(), z.number()]),
})

const usQueueListWithSortSchema = usQueueListSchema.extend({
  sort: z.tuple([z.number(), z.number()]),
})

const queueListSchema = euQueueListWithSortSchema.or(usQueueListWithSortSchema)
export const queueListUnionSchema = z.discriminatedUnion('schema', [
  euQueueListWithSortSchema,
  usQueueListWithSortSchema,
])

export type QueueListType = z.infer<typeof queueListSchema>
export type QueueListUnionType = z.infer<typeof queueListUnionSchema>

export type EUQueueListType = z.infer<typeof euQueueListWithSortSchema>
export type USQueueListType = z.infer<typeof usQueueListWithSortSchema>

// API schemas
export const euQueueAPISchema = euQueueDetailsSchema
  .merge(euQueueListSchema)
  .merge(
    z.object({
      applicationType: z.string(),
      audiences: z.array(IdentifierOnlyEntityItemSchema),
      constructionDate: ISODateTimeOptionalSchema,
      developmentStatus: z.string(),
      localRegion: OptionalStringSchema,
      mwGross: OptionalNumberSchema,
      planningGranted: ISODateTimeOptionalSchema,
      queueDataSetType: z.string(),
      regions: EntityItemArraySchema,
      reportDate: ISODateTimeOptionalSchema,
      sectorDetail: z.string(),
      sourceUpdate: ISODateTimeOptionalSchema,
      stateAbbr: OptionalStringSchema,
      storageType: OptionalStringSchema,
      totals: z.record(z.number()),
      withdrawnDate: ISODateTimeOptionalSchema,
    }),
  )

export const usQueueAPISchema = usQueueDetailsSchema
  .merge(usQueueListSchema)
  .merge(
    z.object({
      audiences: z.array(IdentifierOnlyEntityItemSchema),
      isoRtoUniqueId: z.string(),
      queueDataSetType: z.string(),
      queueKey: z.string(),
      regions: EntityItemArraySchema,
      stateAbbr: OptionalStringSchema,
      stateName: OptionalStringSchema,
      totals: z.record(z.number()),
    }),
  )

export const queueAPISchema = euQueueAPISchema.or(usQueueAPISchema)
export const queueAPIUnionSchema = z.discriminatedUnion('schema', [
  euQueueAPISchema,
  usQueueAPISchema,
])

export type QueueAPIType = z.infer<typeof queueAPISchema>
export type QueueAPIUnionType = z.infer<typeof queueAPIUnionSchema>

export type EUQueueAPIType = z.infer<typeof euQueueAPISchema>
export type USQueueAPIType = z.infer<typeof usQueueAPISchema>
